import type { MapStatus, ValueMap } from "@valuemap/core";

/** Who is signed in: a SalesFix staff member, or a client participant (a guest). */
export interface Me {
  type: "staff" | "guest";
  name: string;
  email: string;
}

export interface MapSummary {
  id: string;
  reference: string;
  title: string;
  status: MapStatus;
  accountName: string | null;
  ownerName: string | null;
  workshopDate: string | null;
  horizon: number | null;
  goalCount: number | null;
  totalExpected: number | null;
  totalYear1: number | null;
  lastModified: string;
}

export interface Permissions {
  edit: boolean;
  delete: boolean;
  import: boolean;
  export: boolean;
  archive: boolean;
  manageParticipants: boolean;
  selfAddAsEditor: boolean;
  lead: boolean;
  /** Owner only: set the status to Final with a version. */
  finalise: boolean;
  /** See the versions and the event log (all staff). */
  history: boolean;
  /** Save a version on request (owner and staff editors). */
  saveVersion: boolean;
}

/** Staff-only context for a map. Never sent to client participants. */
export interface MapDetail extends MapSummary {
  ownerId: string;
  accountId: string | null;
  opportunityId: string | null;
  opportunityName: string | null;
  revision: number;
  map: ValueMap;
  permissions: Permissions;
}

export interface AccountHit {
  id: string;
  name: string;
}

export interface OpportunityHit {
  id: string;
  name: string;
  stage: string | null;
  closed: boolean;
}

export interface MapFilter {
  q?: string;
  status?: MapStatus;
  mine?: boolean;
}

/** An API failure with a message that is safe to show to the person. */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function failure(res: Response): Promise<ApiError> {
  let message = "Something went wrong. Please try again.";
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === "string") message = body.error;
  } catch {
    // Not JSON: keep the generic message.
  }
  return new ApiError(message, res.status);
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T | null> {
  const res = await fetch(url, { credentials: "same-origin", signal });
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) throw await failure(res);
  return (await res.json()) as T;
}

async function sendJson<T>(method: "POST" | "PATCH", url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await failure(res);
  return (await res.json()) as T;
}

/** The signed-in user, or null when signed out. */
export function fetchMe(signal?: AbortSignal): Promise<Me | null> {
  return getJson<Me>("/api/me", signal);
}

export async function fetchMaps(
  filter: MapFilter = {},
  signal?: AbortSignal,
): Promise<MapSummary[]> {
  const params = new URLSearchParams();
  if (filter.q?.trim()) params.set("q", filter.q.trim());
  if (filter.status) params.set("status", filter.status);
  if (filter.mine) params.set("mine", "1");
  const query = params.size ? `?${params}` : "";
  return (await getJson<{ maps: MapSummary[] }>(`/api/maps${query}`, signal))?.maps ?? [];
}

/** One map, or null when it does not exist. */
export function fetchMap(id: string, signal?: AbortSignal): Promise<MapDetail | null> {
  return getJson<MapDetail>(`/api/maps/${encodeURIComponent(id)}`, signal);
}

export async function searchAccounts(q: string, signal?: AbortSignal): Promise<AccountHit[]> {
  return (
    (await getJson<{ accounts: AccountHit[] }>(`/api/accounts?q=${encodeURIComponent(q)}`, signal))
      ?.accounts ?? []
  );
}

export function fetchAccount(id: string, signal?: AbortSignal): Promise<AccountHit | null> {
  return getJson<AccountHit>(`/api/accounts/${encodeURIComponent(id)}`, signal);
}

export async function fetchOpportunities(
  accountId: string,
  signal?: AbortSignal,
): Promise<OpportunityHit[]> {
  return (
    (
      await getJson<{ opportunities: OpportunityHit[] }>(
        `/api/accounts/${encodeURIComponent(accountId)}/opportunities`,
        signal,
      )
    )?.opportunities ?? []
  );
}

export interface NewMapInput {
  title?: string;
  accountId: string;
  opportunityId?: string;
}

export function createMap(input: NewMapInput): Promise<{ id: string }> {
  return sendJson("POST", "/api/maps", input);
}

/** A new map from a reference-tool export (already parsed JSON). */
export function importNewMap(
  input: NewMapInput,
  data: unknown,
): Promise<{ id: string; warnings: string[] }> {
  return sendJson("POST", "/api/maps/import", { ...input, data });
}

/** Replace the content of a Draft map. */
export function importIntoMap(id: string, data: unknown): Promise<{ warnings: string[] }> {
  return sendJson("POST", `/api/maps/${encodeURIComponent(id)}/import`, { data });
}

export function duplicateMap(id: string, input: NewMapInput): Promise<{ id: string }> {
  return sendJson("POST", `/api/maps/${encodeURIComponent(id)}/duplicate`, input);
}

export function renameMap(id: string, title: string): Promise<unknown> {
  return sendJson("PATCH", `/api/maps/${encodeURIComponent(id)}`, { title });
}

export function archiveMap(id: string): Promise<unknown> {
  return sendJson("POST", `/api/maps/${encodeURIComponent(id)}/archive`, {});
}

export const exportUrl = (id: string): string => `/api/maps/${encodeURIComponent(id)}/export`;

/** Set the map to Final, saving a version first. Owner only. */
export function finaliseMap(id: string): Promise<{ status: MapStatus; version: number }> {
  return sendJson("POST", `/api/maps/${encodeURIComponent(id)}/finalise`, {});
}

/** Why a version was taken. */
export type VersionReason = "Workshop closed" | "Finalised" | "Imported" | "Manual";

export interface VersionSummary {
  id: string;
  number: number;
  reason: VersionReason;
  label: string;
  revision: number | null;
  createdAt: string;
}

/** One entry in the map's event log. Staff only: it holds email addresses. */
export interface EventEntry {
  type: string;
  actorEmail: string;
  detail: string | null;
  at: string;
}

export interface History {
  versions: VersionSummary[];
  events: EventEntry[];
}

export function fetchHistory(id: string, signal?: AbortSignal): Promise<History | null> {
  return getJson<History>(`/api/maps/${encodeURIComponent(id)}/history`, signal);
}

/** Save a version now, with an optional name. */
export function saveVersion(id: string, label?: string): Promise<{ version: number }> {
  return sendJson("POST", `/api/maps/${encodeURIComponent(id)}/versions`, label ? { label } : {});
}

export const versionExportUrl = (id: string, versionId: string): string =>
  `/api/maps/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/export`;

// ---------- participants and workshop (staff) ----------

/** A participant as the Share panel shows it. Staff only: includes email addresses. */
export interface Participant {
  id: string;
  email: string;
  name: string;
  type: "Internal" | "External";
  role: "owner" | "editor" | "viewer";
  status: "Invited" | "Active" | "Revoked";
  invitedByName: string | null;
  invitedAt: string | null;
  lastActive: string | null;
}

export interface ParticipantList {
  participants: Participant[];
  permissions: { manage: boolean; selfAdd: boolean; lead: boolean; addColleague: boolean };
}

export interface ContactHit {
  id: string;
  name: string;
  email: string;
}

const mapPath = (id: string, rest = "") => `/api/maps/${encodeURIComponent(id)}${rest}`;

export function fetchParticipants(
  id: string,
  signal?: AbortSignal,
): Promise<ParticipantList | null> {
  return getJson<ParticipantList>(mapPath(id, "/participants"), signal);
}

export async function fetchContacts(id: string, signal?: AbortSignal): Promise<ContactHit[]> {
  return (
    (await getJson<{ contacts: ContactHit[] }>(mapPath(id, "/contacts"), signal))?.contacts ?? []
  );
}

export function inviteParticipant(
  id: string,
  input: { email: string; name: string; role: "editor" | "viewer"; contactId?: string },
): Promise<{ id: string; emailed: boolean }> {
  return sendJson("POST", mapPath(id, "/participants"), input);
}

export function resendInvitation(id: string, participantId: string): Promise<unknown> {
  return sendJson(
    "POST",
    mapPath(id, `/participants/${encodeURIComponent(participantId)}/resend`),
    {},
  );
}

export function revokeParticipant(id: string, participantId: string): Promise<unknown> {
  return sendJson(
    "POST",
    mapPath(id, `/participants/${encodeURIComponent(participantId)}/revoke`),
    {},
  );
}

/** Add yourself as an editor (any staff member, decision D1). */
export function addMeAsEditor(id: string): Promise<unknown> {
  return sendJson("POST", mapPath(id, "/editors"), {});
}

/** The owner adds a SalesFix colleague as an editor, by their Salesforce email address. */
export function addColleague(id: string, email: string): Promise<{ ok: true; name: string }> {
  return sendJson("POST", mapPath(id, "/colleagues"), { email });
}

/** Start or end the live workshop. */
export function setWorkshop(
  id: string,
  live: boolean,
): Promise<{ status: MapStatus; version?: number }> {
  return sendJson("POST", mapPath(id, "/workshop"), { live });
}

// ---------- client participants (guests) ----------

/** A map a client participant is invited to: title and status only. */
export interface GuestMap {
  id: string;
  title: string;
  status: MapStatus;
  role: "editor" | "viewer";
}

export async function fetchGuestConfig(
  signal?: AbortSignal,
): Promise<{ turnstileSiteKey: string | null }> {
  return (
    (await getJson<{ turnstileSiteKey: string | null }>("/api/guest/config", signal)) ?? {
      turnstileSiteKey: null,
    }
  );
}

export function requestCode(input: {
  email: string;
  mapId?: string;
  turnstileToken?: string;
}): Promise<{ message: string }> {
  return sendJson("POST", "/api/guest/code", input);
}

export function verifyCode(input: { email: string; code: string }): Promise<unknown> {
  return sendJson("POST", "/api/guest/verify", input);
}

export async function fetchGuestMaps(signal?: AbortSignal): Promise<GuestMap[]> {
  return (await getJson<{ maps: GuestMap[] }>("/api/guest/maps", signal))?.maps ?? [];
}
